import {
    Add,
    DeleteOutline,
    LocalOfferOutlined,
    QrCodeScanner,
    Remove,
    Replay,
} from "@mui/icons-material";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import React, {
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../AppContext";
import config from "../../../config";
import {
    useExchangeRetailReturn,
    useLoadRetailReturnInvoice,
    usePreviewRetailExchange,
    useSearchRetailReturnInvoices,
    useSubmitRetailReturn,
} from "../../../shared/hooks/useInvoicesData";
import { usePrintOrder } from "../../../shared/hooks/useTablesData";
import { defaultErrorToast } from "../../../utils/floorUtils";
import { resolvePosProfileDiscountPercentages } from "../../../utils/discountOptions";
import {
    buildDownloadPdfUrl,
    getPosInvoicePrintFormat,
    getProfileLetterhead,
} from "../../../utils/printFormatUrl";
import {
    applyReplacementItemDiscounts,
    buildExchangePaymentDetails,
    buildReplacementItemsFromCart,
    getReplacementItemKey,
    getRetailReturnPolicy,
    isReplacementDiscountAcknowledged,
    resolveScannedInvoiceItemCode,
} from "./retailReturnUtils";

const getImageUrl = (image) => {
    if (!image) return "";
    return String(image).startsWith("http")
        ? image
        : `${config.baseUrl}${image}`;
};

const formatAmount = (value, currency) => {
    const amount = Number(value || 0);
    return `${Number.isFinite(amount) ? amount.toFixed(2) : "0.00"}${
        currency ? ` ${currency}` : ""
    }`;
};

const resolveBarcodeItemCode = async (scanValue) => {
    const { data } = await axios.post(
        "method/erpnext.selling.page.point_of_sale.point_of_sale.search_for_serial_or_batch_or_barcode_number",
        { search_value: scanValue }
    );
    return data?.message?.item_code || null;
};

export default function RetailReturnDialog({
    open,
    embedded = false,
    onClose,
    posProfile,
    isAdminRole = false,
    replacementCartItems = [],
    replacementCartBlocked = false,
    onChangeReplacementQty,
    onReturned,
}) {
    const { t } = useTranslation();
    const { restaurantSettings } = useContext(AppContext);
    const scanInputRef = useRef(null);
    const scanInFlightRef = useRef(false);
    const submitInFlightRef = useRef(false);
    const exchangeQuoteRequestRef = useRef(0);
    const loadInvoiceMutation = useLoadRetailReturnInvoice();
    const searchInvoicesMutation = useSearchRetailReturnInvoices();
    const submitReturnMutation = useSubmitRetailReturn();
    const exchangeReturnMutation = useExchangeRetailReturn();
    const exchangePreviewMutation = usePreviewRetailExchange();
    const printReturnInvoiceMutation = usePrintOrder();
    const previewExchangeRef = useRef(exchangePreviewMutation.mutateAsync);
    previewExchangeRef.current = exchangePreviewMutation.mutateAsync;
    const returnPolicy = useMemo(
        () => getRetailReturnPolicy(posProfile, isAdminRole),
        [isAdminRole, posProfile]
    );
    const baseReplacementItems = useMemo(
        () => buildReplacementItemsFromCart(replacementCartItems),
        [replacementCartItems]
    );
    const replacementDiscountAllowed =
        Number(posProfile?.rm_allow_on_demand_discount) === 1;
    const replacementDiscountOptions = useMemo(() => {
        const configuredOptions = resolvePosProfileDiscountPercentages(
            posProfile?.rm_discount_options
        );
        return configuredOptions.includes(0)
            ? configuredOptions
            : [0, ...configuredOptions];
    }, [posProfile?.rm_discount_options]);
    const [scanValue, setScanValue] = useState("");
    const [returnData, setReturnData] = useState(null);
    const [invoiceMatches, setInvoiceMatches] = useState([]);
    const [selectedQty, setSelectedQty] = useState({});
    const [scannedItemsMode, setScannedItemsMode] = useState(false);
    const [isResolvingItem, setIsResolvingItem] = useState(false);
    const [returnMode, setReturnMode] = useState(returnPolicy.defaultMode);
    const [exchangeQuote, setExchangeQuote] = useState(null);
    const [exchangeQuoteLoading, setExchangeQuoteLoading] = useState(false);
    const [exchangeQuoteError, setExchangeQuoteError] = useState(null);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState("");
    const [replacementDiscountOpen, setReplacementDiscountOpen] =
        useState(false);
    const [replacementDiscountMode, setReplacementDiscountMode] =
        useState("order");
    const [
        replacementOrderDiscountPercentage,
        setReplacementOrderDiscountPercentage,
    ] = useState(0);
    const [replacementItemDiscounts, setReplacementItemDiscounts] = useState(
        {}
    );
    const activeReplacementOrderDiscountPercentage =
        replacementDiscountAllowed &&
        replacementDiscountOpen &&
        replacementDiscountMode === "order"
            ? replacementOrderDiscountPercentage
            : 0;
    const activeReplacementItemDiscounts = useMemo(
        () =>
            replacementDiscountAllowed &&
            replacementDiscountOpen &&
            replacementDiscountMode === "items"
                ? replacementItemDiscounts
                : {},
        [
            replacementDiscountAllowed,
            replacementDiscountMode,
            replacementDiscountOpen,
            replacementItemDiscounts,
        ]
    );
    const replacementItems = useMemo(
        () =>
            applyReplacementItemDiscounts(
                baseReplacementItems,
                activeReplacementItemDiscounts
            ),
        [baseReplacementItems, activeReplacementItemDiscounts]
    );

    const focusScanner = () => {
        window.requestAnimationFrame(() => scanInputRef.current?.focus());
    };

    const resetReturn = () => {
        setScanValue("");
        setReturnData(null);
        setInvoiceMatches([]);
        setSelectedQty({});
        setScannedItemsMode(false);
        setIsResolvingItem(false);
        setExchangeQuote(null);
        setExchangeQuoteLoading(false);
        setExchangeQuoteError(null);
        setSelectedPaymentMode("");
        setReplacementDiscountOpen(false);
        setReplacementDiscountMode("order");
        setReplacementOrderDiscountPercentage(0);
        setReplacementItemDiscounts({});
    };

    useEffect(() => {
        if (open) {
            setReturnMode(returnPolicy.defaultMode);
            focusScanner();
        } else {
            resetReturn();
        }
    }, [open, returnPolicy.defaultMode]);

    useEffect(() => {
        if (replacementDiscountAllowed) return;
        setReplacementDiscountOpen(false);
        setReplacementOrderDiscountPercentage(0);
        setReplacementItemDiscounts({});
    }, [replacementDiscountAllowed]);

    useEffect(() => {
        const activeItemKeys = new Set(
            baseReplacementItems.map((item) => getReplacementItemKey(item))
        );
        setReplacementItemDiscounts((current) => {
            const nextDiscounts = Object.fromEntries(
                Object.entries(current).filter(([itemKey]) =>
                    activeItemKeys.has(itemKey)
                )
            );
            return Object.keys(nextDiscounts).length ===
                Object.keys(current).length
                ? current
                : nextDiscounts;
        });
    }, [baseReplacementItems]);

    const selectedItems = useMemo(
        () =>
            (returnData?.items || [])
                .map((item) => ({
                    ...item,
                    selected_qty: Number(selectedQty[item.invoice_item] || 0),
                }))
                .filter((item) => item.selected_qty > 0),
        [returnData?.items, selectedQty]
    );

    const selectedUnitCount = useMemo(
        () =>
            selectedItems.reduce((total, item) => total + item.selected_qty, 0),
        [selectedItems]
    );

    const returnItemsPayload = useMemo(
        () =>
            selectedItems.map((item) => ({
                invoice_item: item.invoice_item,
                qty: item.selected_qty,
            })),
        [selectedItems]
    );

    const paymentModeOptions = useMemo(() => {
        const quoteModes = Array.isArray(exchangeQuote?.payment_modes)
            ? exchangeQuote.payment_modes
            : [];
        const profileModes = (posProfile?.payments || [])
            .map((payment) => payment?.mode_of_payment)
            .filter(Boolean);
        return [...new Set([...quoteModes, ...profileModes])];
    }, [exchangeQuote?.payment_modes, posProfile?.payments]);

    useEffect(() => {
        if (!open || !paymentModeOptions.length) return;
        setSelectedPaymentMode((current) => {
            if (paymentModeOptions.includes(current)) return current;
            const profileDefault = posProfile?.payments?.find(
                (payment) => Number(payment?.default) === 1
            )?.mode_of_payment;
            return (
                exchangeQuote?.default_payment_mode ||
                profileDefault ||
                paymentModeOptions[0]
            );
        });
    }, [
        exchangeQuote?.default_payment_mode,
        open,
        paymentModeOptions,
        posProfile?.payments,
    ]);

    useEffect(() => {
        const requestId = exchangeQuoteRequestRef.current + 1;
        exchangeQuoteRequestRef.current = requestId;

        const canQuote =
            open &&
            returnMode === "exchange" &&
            returnData?.invoice?.name &&
            returnItemsPayload.length > 0 &&
            replacementItems.length > 0 &&
            !replacementCartBlocked;

        if (!canQuote) {
            setExchangeQuote(null);
            setExchangeQuoteLoading(false);
            setExchangeQuoteError(null);
            return undefined;
        }

        // Exchange totals are authoritative financial values. Clear the stale
        // quote while the server recalculates instead of briefly presenting an
        // optimistic amount that can be replaced by a different server total.
        setExchangeQuote(null);
        setExchangeQuoteLoading(true);
        setExchangeQuoteError(null);

        const timeoutId = window.setTimeout(() => {
            previewExchangeRef.current({
                invoice: returnData.invoice.name,
                posProfile: posProfile?.name,
                returnItems: returnItemsPayload,
                replacementItems,
                replacementOrderDiscountPercentage:
                    activeReplacementOrderDiscountPercentage,
            })
                .then((quote) => {
                    if (exchangeQuoteRequestRef.current !== requestId) return;
                    if (
                        !isReplacementDiscountAcknowledged({
                            quote,
                            replacementItems,
                            replacementOrderDiscountPercentage:
                                activeReplacementOrderDiscountPercentage,
                        })
                    ) {
                        setExchangeQuote(null);
                        setExchangeQuoteError("discount");
                        return;
                    }
                    setExchangeQuote(quote);
                })
                .catch(() => {
                    if (exchangeQuoteRequestRef.current !== requestId) return;
                    setExchangeQuote(null);
                    const hasRequestedDiscount =
                        activeReplacementOrderDiscountPercentage > 0 ||
                        replacementItems.some(
                            (item) => Number(item?.discount_percentage) > 0
                        );
                    setExchangeQuoteError(
                        hasRequestedDiscount ? "discount" : "request"
                    );
                })
                .finally(() => {
                    if (exchangeQuoteRequestRef.current !== requestId) return;
                    setExchangeQuoteLoading(false);
                });
        }, 250);

        return () => window.clearTimeout(timeoutId);
    }, [
        open,
        activeReplacementItemDiscounts,
        activeReplacementOrderDiscountPercentage,
        posProfile,
        replacementCartBlocked,
        replacementCartItems,
        replacementItems,
        returnData?.invoice?.currency,
        returnData?.invoice?.name,
        returnItemsPayload,
        returnMode,
        selectedItems,
    ]);

    const exchangePaymentDue = Number(exchangeQuote?.payment_due || 0);
    const exchangeCurrency =
        exchangeQuote?.currency || returnData?.invoice?.currency || "";
    const exchangeRequiresPayment = exchangePaymentDue > 0.0001;

    const printReturnInvoice = async (invoiceName) => {
        if (
            !invoiceName ||
            Number(posProfile?.optional_printing) === 1 ||
            Number(
                restaurantSettings?.docs?.[0]?.dont_print_invoices
            ) === 1
        )
            return;

        const printFormat = getPosInvoicePrintFormat(posProfile);
        const printer = posProfile?.invoice_printer;
        if (printer) {
            await printReturnInvoiceMutation.mutateAsync({
                doctype: "POS Invoice",
                orderId: invoiceName,
                printFormat,
                printer,
            });
            return;
        }

        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = buildDownloadPdfUrl({
            doctype: "POS Invoice",
            name: invoiceName,
            format: printFormat,
            noLetterhead: 0,
            letterhead: getProfileLetterhead(posProfile),
        });
        document.body.appendChild(iframe);
        iframe.onload = () => {
            try {
                iframe.contentWindow.print();
            } catch (error) {
                enqueueSnackbar(t("retailReturn.returnPrintFailed"), {
                    variant: "warning",
                });
            }
        };
    };

    const selectAllReturnableItems = (data = returnData) => {
        const nextSelection = {};
        (data?.items || []).forEach((item) => {
            nextSelection[item.invoice_item] = Number(item.available_qty || 0);
        });
        setSelectedQty(nextSelection);
        setScannedItemsMode(false);
    };

    const notifyScannerCorrection = (ignoredSuffix) => {
        if (!ignoredSuffix) return;
        enqueueSnackbar(
            t("retailReturn.scannerSuffixIgnored", {
                suffix: ignoredSuffix,
            }),
            { variant: "info" }
        );
    };

    const loadInvoice = async (invoiceBarcode) => {
        try {
            const data = await loadInvoiceMutation.mutateAsync({
                invoiceBarcode,
                posProfile: posProfile?.name,
            });
            setReturnData(data);
            setInvoiceMatches([]);
            selectAllReturnableItems(data);
            notifyScannerCorrection(data?.scan_correction?.ignored_suffix);
            if (!data?.items?.length) {
                enqueueSnackbar(t("retailReturn.noReturnableItems"), {
                    variant: "warning",
                });
            }
        } catch (error) {
            defaultErrorToast(error);
        } finally {
            setScanValue("");
            focusScanner();
        }
    };

    const findInvoiceBySuffix = async (suffix) => {
        try {
            const result = await searchInvoicesMutation.mutateAsync({
                suffix,
                posProfile: posProfile?.name,
            });
            const matches = result?.matches || [];
            notifyScannerCorrection(result?.scan_correction?.ignored_suffix);
            if (matches.length === 1) {
                await loadInvoice(matches[0].name);
                return;
            }
            if (matches.length > 1) {
                setInvoiceMatches(matches);
                setScanValue("");
                return;
            }
            setInvoiceMatches([]);
            enqueueSnackbar(
                t("retailReturn.invoiceSearchEmpty", { suffix }),
                { variant: "warning" }
            );
            setScanValue("");
            focusScanner();
        } catch (error) {
            defaultErrorToast(error);
        }
    };

    const scanInvoiceItem = async (itemBarcode) => {
        setIsResolvingItem(true);
        try {
            let itemCode = null;
            let correction = null;
            try {
                const resolution = await resolveScannedInvoiceItemCode({
                    scanValue: itemBarcode,
                    invoiceItems: returnData?.items,
                    resolveBarcodeItemCode,
                });
                itemCode = resolution.itemCode;
                correction = resolution.correction;
            } catch {
                enqueueSnackbar(t("retailReturn.itemNotInInvoice"), {
                    variant: "error",
                });
                return;
            }
            const matchingItems = (returnData?.items || []).filter(
                (item) => item.item_code === itemCode
            );

            if (!itemCode || !matchingItems.length) {
                enqueueSnackbar(t("retailReturn.itemNotInInvoice"), {
                    variant: "error",
                });
                return;
            }

            const baseSelection = scannedItemsMode ? { ...selectedQty } : {};
            const targetItem = matchingItems.find(
                (item) =>
                    Number(baseSelection[item.invoice_item] || 0) <
                    Number(item.available_qty || 0)
            );

            if (!targetItem) {
                enqueueSnackbar(t("retailReturn.maxQtySelected"), {
                    variant: "warning",
                });
                return;
            }

            baseSelection[targetItem.invoice_item] =
                Number(baseSelection[targetItem.invoice_item] || 0) + 1;
            setSelectedQty(baseSelection);
            setScannedItemsMode(true);
            notifyScannerCorrection(correction?.ignoredSuffix);
            enqueueSnackbar(
                t("retailReturn.itemAdded", { item: targetItem.item_code }),
                { variant: "success" }
            );
        } catch (error) {
            defaultErrorToast(error);
        } finally {
            setIsResolvingItem(false);
            setScanValue("");
            focusScanner();
        }
    };

    const handleScan = async () => {
        const normalizedScan = String(scanValue || "").trim();
        if (!normalizedScan || scanInFlightRef.current) return;

        scanInFlightRef.current = true;
        try {
            if (!returnData) {
                await findInvoiceBySuffix(normalizedScan);
            } else {
                await scanInvoiceItem(normalizedScan);
            }
        } finally {
            scanInFlightRef.current = false;
        }
    };

    const updateSelectedQty = (item, nextQty) => {
        const normalizedQty = Math.max(
            0,
            Math.min(Number(nextQty || 0), Number(item.available_qty || 0))
        );
        setSelectedQty((current) => ({
            ...current,
            [item.invoice_item]: normalizedQty,
        }));
    };

    const toggleReplacementDiscount = () => {
        if (replacementDiscountOpen) {
            setReplacementOrderDiscountPercentage(0);
            setReplacementItemDiscounts({});
        }
        setReplacementDiscountOpen(!replacementDiscountOpen);
    };

    const changeReplacementDiscountMode = (_, nextMode) => {
        if (!nextMode || nextMode === replacementDiscountMode) return;
        setReplacementDiscountMode(nextMode);
        setReplacementOrderDiscountPercentage(0);
        setReplacementItemDiscounts({});
    };

    const setReplacementItemDiscount = (item, percentage) => {
        const itemKey = getReplacementItemKey(item);
        if (!itemKey) return;
        const normalizedPercentage = Number(percentage) || 0;
        setReplacementItemDiscounts((current) => {
            const nextDiscounts = { ...current };
            if (normalizedPercentage > 0) {
                nextDiscounts[itemKey] = normalizedPercentage;
            } else {
                delete nextDiscounts[itemKey];
            }
            return nextDiscounts;
        });
    };

    const handleSubmit = async () => {
        if (
            !returnData?.invoice?.name ||
            !selectedItems.length ||
            submitInFlightRef.current
        )
            return;

        submitInFlightRef.current = true;
        try {
            let result;

            if (returnMode === "exchange") {
                if (
                    replacementCartBlocked ||
                    !replacementItems.length ||
                    !exchangeQuote?.is_value_sufficient ||
                    (exchangeQuoteLoading && !exchangeQuote) ||
                    exchangeQuoteError ||
                    (exchangeRequiresPayment && !selectedPaymentMode)
                )
                    return;
                result = await exchangeReturnMutation.mutateAsync({
                    invoice: returnData.invoice.name,
                    posProfile: posProfile?.name,
                    returnItems: returnItemsPayload,
                    replacementItems,
                    replacementOrderDiscountPercentage:
                        activeReplacementOrderDiscountPercentage,
                    paymentDetails: buildExchangePaymentDetails(
                        exchangeQuote,
                        selectedPaymentMode
                    ),
                });
                enqueueSnackbar(
                    t("retailReturn.exchangeCreated", {
                        returnInvoice: result?.return_invoice,
                        replacementInvoice: result?.replacement_invoice,
                    }),
                    { variant: "success" }
                );
            } else {
                if (!returnPolicy.cashReturnAllowed) return;
                result = await submitReturnMutation.mutateAsync({
                    invoice: returnData.invoice.name,
                    posProfile: posProfile?.name,
                    items: returnItemsPayload,
                });
                enqueueSnackbar(
                    t("retailReturn.returnCreated", { invoice: result?.name }),
                    { variant: "success" }
                );
            }

            const invoiceToPrint =
                returnMode === "exchange"
                    ? result?.replacement_invoice ||
                      result?.return_invoice ||
                      result?.name
                    : result?.name || result?.return_invoice;
            try {
                await printReturnInvoice(invoiceToPrint);
            } catch (error) {
                enqueueSnackbar(t("retailReturn.returnPrintFailed"), {
                    variant: "warning",
                });
            }
            onReturned?.(result);
            onClose?.();
        } catch (error) {
            defaultErrorToast(error);
        } finally {
            submitInFlightRef.current = false;
        }
    };

    const busy =
        loadInvoiceMutation.isLoading ||
        searchInvoicesMutation.isLoading ||
        submitReturnMutation.isLoading ||
        exchangeReturnMutation.isLoading ||
        printReturnInvoiceMutation.isLoading ||
        isResolvingItem;
    const exchangeUnavailable =
        returnMode === "exchange" &&
        (replacementCartBlocked ||
            !replacementItems.length ||
            (exchangeQuoteLoading && !exchangeQuote) ||
            exchangeQuoteError ||
            !exchangeQuote?.is_value_sufficient ||
            (exchangeRequiresPayment && !selectedPaymentMode));

    const returnContent = (
        <React.Fragment>
            <DialogContent dividers>
                <Stack spacing={2}>
                    {embedded ? (
                        <Typography variant="subtitle1" fontWeight={900}>
                            {t(
                                returnData
                                    ? "retailReturn.returnedItemsTitle"
                                    : "retailReturn.startReturnTitle"
                            )}
                        </Typography>
                    ) : null}
                    <TextField
                        inputRef={scanInputRef}
                        value={scanValue}
                        onChange={(event) => setScanValue(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                event.preventDefault();
                                handleScan();
                            }
                        }}
                        disabled={busy}
                        fullWidth
                        label={
                            returnData
                                ? t("retailReturn.scanItem")
                                : t("retailReturn.scanInvoice")
                        }
                        placeholder={
                            returnData
                                ? t("retailReturn.scanItemPlaceholder")
                                : t("retailReturn.scanInvoicePlaceholder")
                        }
                        InputProps={{
                            startAdornment: (
                                <QrCodeScanner
                                    sx={{ mr: 1, color: "text.secondary" }}
                                />
                            ),
                            endAdornment: busy ? (
                                <CircularProgress size={20} />
                            ) : embedded ? null : (
                                <Button
                                    onClick={handleScan}
                                    disabled={!scanValue.trim()}
                                >
                                    {t("retailReturn.scan")}
                                </Button>
                            ),
                        }}
                    />
                    {embedded ? (
                        <Button
                            variant="outlined"
                            onClick={handleScan}
                            disabled={busy || !scanValue.trim()}
                            fullWidth
                        >
                            {t("retailReturn.scan")}
                        </Button>
                    ) : null}

                    {!returnData && invoiceMatches.length > 1 ? (
                        <Paper variant="outlined">
                            <Box sx={{ px: 2, pt: 1.5 }}>
                                <Typography fontWeight={800}>
                                    {t("retailReturn.invoiceMatches")}
                                </Typography>
                            </Box>
                            <List disablePadding>
                                {invoiceMatches.map((invoice) => (
                                    <ListItemButton
                                        key={invoice.name}
                                        onClick={() => loadInvoice(invoice.name)}
                                        disabled={busy}
                                    >
                                        <ListItemText
                                            primary={invoice.name}
                                            secondary={`${
                                                invoice.customer_name ||
                                                invoice.customer ||
                                                "-"
                                            } • ${invoice.posting_date || "-"} • ${invoice.company || "-"} • ${invoice.pos_profile || "-"}`}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Paper>
                    ) : null}

                    {!returnData ? (
                        <Alert severity="info">
                            {t("retailReturn.scanInvoiceHelp")}
                        </Alert>
                    ) : (
                        <React.Fragment>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    justifyContent="space-between"
                                    spacing={1}
                                >
                                    <Box>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={800}
                                        >
                                            {returnData.invoice.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {returnData.invoice.customer_name ||
                                                returnData.invoice.customer}
                                        </Typography>
                                    </Box>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <Chip
                                            color={
                                                scannedItemsMode
                                                    ? "primary"
                                                    : "default"
                                            }
                                            label={
                                                scannedItemsMode
                                                    ? t(
                                                          "retailReturn.scannedItemsMode"
                                                      )
                                                    : t(
                                                          "retailReturn.allItemsMode"
                                                      )
                                            }
                                        />
                                        <Button
                                            size="small"
                                            startIcon={<Replay />}
                                            onClick={() =>
                                                selectAllReturnableItems()
                                            }
                                        >
                                            {t("retailReturn.restoreAll")}
                                        </Button>
                                        <Button
                                            size="small"
                                            color="inherit"
                                            onClick={resetReturn}
                                        >
                                            {t("retailReturn.changeInvoice")}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Paper>

                            <Alert
                                severity={scannedItemsMode ? "info" : "warning"}
                            >
                                {scannedItemsMode
                                    ? t("retailReturn.scannedItemsHelp")
                                    : t("retailReturn.allItemsHelp")}
                            </Alert>

                            {returnPolicy.cashReturnAllowed ? (
                                <ToggleButtonGroup
                                    exclusive
                                    fullWidth
                                    value={returnMode}
                                    onChange={(_, nextMode) => {
                                        if (nextMode) setReturnMode(nextMode);
                                    }}
                                    disabled={busy}
                                    color="primary"
                                >
                                    <ToggleButton value="exchange">
                                        {t("retailReturn.exchangeMode")}
                                    </ToggleButton>
                                    <ToggleButton value="cash">
                                        {t("retailReturn.cashReturnMode")}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            ) : (
                                <Alert severity="info">
                                    {t("retailReturn.exchangeRequired")}
                                </Alert>
                            )}

                            {returnMode === "exchange" ? (
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Stack spacing={1.25}>
                                        <Typography fontWeight={800}>
                                            {t(
                                                embedded
                                                    ? "retailReturn.addedItemsTitle"
                                                    : "retailReturn.replacementCartTitle"
                                            )}
                                        </Typography>
                                        {replacementCartBlocked ? (
                                            <Alert severity="error">
                                                {t(
                                                    "retailReturn.savedCartBlocked"
                                                )}
                                            </Alert>
                                        ) : (
                                            <React.Fragment>
                                                {replacementDiscountAllowed &&
                                                replacementItems.length ? (
                                                    <Stack spacing={1}>
                                                        <Button
                                                            variant={
                                                                replacementDiscountOpen
                                                                    ? "contained"
                                                                    : "outlined"
                                                            }
                                                            startIcon={
                                                                <LocalOfferOutlined />
                                                            }
                                                            onClick={
                                                                toggleReplacementDiscount
                                                            }
                                                            disabled={busy}
                                                            sx={{
                                                                alignSelf:
                                                                    "flex-start",
                                                            }}
                                                        >
                                                            {t(
                                                                replacementDiscountOpen
                                                                    ? "retailReturn.hideReplacementDiscount"
                                                                    : "retailReturn.addReplacementDiscount"
                                                            )}
                                                        </Button>
                                                        {replacementDiscountOpen ? (
                                                            <React.Fragment>
                                                                <Alert severity="info">
                                                                    {t(
                                                                        "retailReturn.replacementDiscountHelp"
                                                                    )}
                                                                </Alert>
                                                                <ToggleButtonGroup
                                                                    exclusive
                                                                    fullWidth
                                                                    size="small"
                                                                    value={
                                                                        replacementDiscountMode
                                                                    }
                                                                    onChange={
                                                                        changeReplacementDiscountMode
                                                                    }
                                                                    disabled={busy}
                                                                    color="primary"
                                                                >
                                                                    <ToggleButton value="order">
                                                                        {t(
                                                                            "retailReturn.discountWholeReplacement"
                                                                        )}
                                                                    </ToggleButton>
                                                                    <ToggleButton value="items">
                                                                        {t(
                                                                            "retailReturn.discountReplacementItems"
                                                                        )}
                                                                    </ToggleButton>
                                                                </ToggleButtonGroup>
                                                                {replacementDiscountMode ===
                                                                "order" ? (
                                                                    <TextField
                                                                        select
                                                                        fullWidth
                                                                        size="small"
                                                                        label={t(
                                                                            "retailReturn.orderDiscountPercentage"
                                                                        )}
                                                                        value={
                                                                            replacementOrderDiscountPercentage
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            setReplacementOrderDiscountPercentage(
                                                                                Number(
                                                                                    event
                                                                                        .target
                                                                                        .value
                                                                                ) ||
                                                                                    0
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            busy
                                                                        }
                                                                    >
                                                                        {replacementDiscountOptions.map(
                                                                            (
                                                                                percentage
                                                                            ) => (
                                                                                <MenuItem
                                                                                    key={
                                                                                        percentage
                                                                                    }
                                                                                    value={
                                                                                        percentage
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        percentage
                                                                                    }
                                                                                    %
                                                                                </MenuItem>
                                                                            )
                                                                        )}
                                                                    </TextField>
                                                                ) : (
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                    >
                                                                        {t(
                                                                            "retailReturn.itemDiscountHelp"
                                                                        )}
                                                                    </Typography>
                                                                )}
                                                            </React.Fragment>
                                                        ) : null}
                                                    </Stack>
                                                ) : null}
                                                {!replacementItems.length ? (
                                                    <Alert severity="info">
                                                        {t(
                                                            "retailReturn.replacementCartEmpty"
                                                        )}
                                                    </Alert>
                                                ) : (
                                                    <Stack
                                                        divider={
                                                            <Divider flexItem />
                                                        }
                                                    >
                                                        {replacementItems.map(
                                                            (item) => (
                                                                <Box
                                                                    key={`${
                                                                        item.item_code
                                                                    }:${
                                                                        item.uom ||
                                                                        ""
                                                                    }`}
                                                                    sx={{
                                                                        py: 0.75,
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        gap: 1,
                                                                        flexWrap:
                                                                            "wrap",
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            flex: 1,
                                                                            minWidth: 0,
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            fontWeight={
                                                                                700
                                                                            }
                                                                            noWrap
                                                                        >
                                                                            {
                                                                                item.item_code
                                                                            }
                                                                        </Typography>
                                                                        {item.uom ? (
                                                                            <Typography
                                                                                variant="body2"
                                                                                color="text.secondary"
                                                                            >
                                                                                {
                                                                                    item.uom
                                                                                }
                                                                            </Typography>
                                                                        ) : null}
                                                                    </Box>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() =>
                                                                            onChangeReplacementQty?.(
                                                                                item,
                                                                                -1
                                                                            )
                                                                        }
                                                                        aria-label={t(
                                                                            "retailReturn.decreaseReplacement"
                                                                        )}
                                                                    >
                                                                        <Remove fontSize="small" />
                                                                    </IconButton>
                                                                    <Typography
                                                                        sx={{
                                                                            minWidth: 36,
                                                                            textAlign:
                                                                                "center",
                                                                        }}
                                                                    >
                                                                        {item.qty}
                                                                    </Typography>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() =>
                                                                            onChangeReplacementQty?.(
                                                                                item,
                                                                                1
                                                                            )
                                                                        }
                                                                        aria-label={t(
                                                                            "retailReturn.increaseReplacement"
                                                                        )}
                                                                    >
                                                                        <Add fontSize="small" />
                                                                    </IconButton>
                                                                    {replacementDiscountOpen &&
                                                                    replacementDiscountMode ===
                                                                        "items" ? (
                                                                        <TextField
                                                                            select
                                                                            size="small"
                                                                            label={t(
                                                                                "retailReturn.itemDiscountPercentage"
                                                                            )}
                                                                            value={
                                                                                replacementItemDiscounts[
                                                                                    getReplacementItemKey(
                                                                                        item
                                                                                    )
                                                                                ] ||
                                                                                0
                                                                            }
                                                                            onChange={(
                                                                                event
                                                                            ) =>
                                                                                setReplacementItemDiscount(
                                                                                    item,
                                                                                    event
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                busy
                                                                            }
                                                                            sx={{
                                                                                minWidth: 135,
                                                                            }}
                                                                        >
                                                                            {replacementDiscountOptions.map(
                                                                                (
                                                                                    percentage
                                                                                ) => (
                                                                                    <MenuItem
                                                                                        key={
                                                                                            percentage
                                                                                        }
                                                                                        value={
                                                                                            percentage
                                                                                        }
                                                                                    >
                                                                                        {
                                                                                            percentage
                                                                                        }
                                                                                        %
                                                                                    </MenuItem>
                                                                                )
                                                                            )}
                                                                        </TextField>
                                                                    ) : null}
                                                                </Box>
                                                            )
                                                        )}
                                                    </Stack>
                                                )}
                                            </React.Fragment>
                                        )}

                                        {exchangeQuoteLoading && !exchangeQuote ? (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                            >
                                                <CircularProgress size={18} />
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {t(
                                                        "retailReturn.calculatingExchange"
                                                    )}
                                                </Typography>
                                            </Stack>
                                        ) : exchangeQuoteError ? (
                                            <Alert severity="error">
                                                {t(
                                                    exchangeQuoteError ===
                                                        "discount"
                                                        ? "retailReturn.exchangeDiscountNotApplied"
                                                        : "retailReturn.exchangeQuoteFailed"
                                                )}
                                            </Alert>
                                        ) : exchangeQuote ? (
                                            <React.Fragment>
                                                {exchangeQuote.is_estimated ? (
                                                    <Alert
                                                        severity={
                                                            exchangeQuoteLoading
                                                                ? "info"
                                                                : "warning"
                                                        }
                                                    >
                                                        {t(
                                                            exchangeQuoteLoading
                                                                ? "retailReturn.calculatingExchange"
                                                                : "retailReturn.exchangeQuoteEstimated"
                                                        )}
                                                    </Alert>
                                                ) : null}
                                                <Stack
                                                    direction={{
                                                        xs: "column",
                                                        sm: "row",
                                                    }}
                                                    spacing={2}
                                                    useFlexGap
                                                >
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {t(
                                                                "retailReturn.returnCredit"
                                                            )}
                                                        </Typography>
                                                        <Typography fontWeight={800}>
                                                            {formatAmount(
                                                                exchangeQuote.return_credit,
                                                                exchangeCurrency
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {t(
                                                                "retailReturn.replacementTotal"
                                                            )}
                                                        </Typography>
                                                        <Typography fontWeight={800}>
                                                            {formatAmount(
                                                                exchangeQuote.replacement_total,
                                                                exchangeCurrency
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                    {exchangeRequiresPayment ? (
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                {t(
                                                                    "retailReturn.amountDue"
                                                                )}
                                                            </Typography>
                                                            <Typography
                                                                fontWeight={800}
                                                                color="error.main"
                                                            >
                                                                {formatAmount(
                                                                    exchangePaymentDue,
                                                                    exchangeCurrency
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    ) : null}
                                                </Stack>

                                                {!exchangeQuote.is_value_sufficient ? (
                                                    <Alert severity="warning">
                                                        {t(
                                                            "retailReturn.exchangeValueShort",
                                                            {
                                                                amount: formatAmount(
                                                                    Number(
                                                                        exchangeQuote.return_credit ||
                                                                            0
                                                                    ) -
                                                                        Number(
                                                                            exchangeQuote.replacement_total ||
                                                                                0
                                                                        ),
                                                                    exchangeCurrency
                                                                ),
                                                            }
                                                        )}
                                                    </Alert>
                                                ) : exchangeRequiresPayment ? (
                                                    <Alert severity="info">
                                                        {t(
                                                            "retailReturn.exchangeDifferenceDue",
                                                            {
                                                                amount: formatAmount(
                                                                    exchangePaymentDue,
                                                                    exchangeCurrency
                                                                ),
                                                            }
                                                        )}
                                                    </Alert>
                                                ) : Number(
                                                      exchangeQuote.refund_amount ||
                                                          0
                                                  ) > 0.0001 ? (
                                                    <Alert severity="info">
                                                        {t(
                                                            "retailReturn.lowerValueRefund",
                                                            {
                                                                amount: formatAmount(
                                                                    exchangeQuote.refund_amount,
                                                                    exchangeCurrency
                                                                ),
                                                            }
                                                        )}
                                                    </Alert>
                                                ) : (
                                                    <Alert severity="success">
                                                        {t(
                                                            "retailReturn.exchangeValueMatched"
                                                        )}
                                                    </Alert>
                                                )}

                                                {exchangeQuote.is_value_sufficient &&
                                                exchangeRequiresPayment ? (
                                                    paymentModeOptions.length ? (
                                                        <FormControl fullWidth>
                                                            <InputLabel id="retail-exchange-payment-mode-label">
                                                                {t(
                                                                    "retailReturn.paymentMode"
                                                                )}
                                                            </InputLabel>
                                                            <Select
                                                                labelId="retail-exchange-payment-mode-label"
                                                                value={
                                                                    selectedPaymentMode
                                                                }
                                                                label={t(
                                                                    "retailReturn.paymentMode"
                                                                )}
                                                                onChange={(event) =>
                                                                    setSelectedPaymentMode(
                                                                        event.target
                                                                            .value
                                                                    )
                                                                }
                                                                disabled={busy}
                                                            >
                                                                {paymentModeOptions.map(
                                                                    (mode) => (
                                                                        <MenuItem
                                                                            key={
                                                                                mode
                                                                            }
                                                                            value={
                                                                                mode
                                                                            }
                                                                        >
                                                                            {
                                                                                mode
                                                                            }
                                                                        </MenuItem>
                                                                    )
                                                                )}
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        <Alert severity="error">
                                                            {t(
                                                                "retailReturn.paymentModeMissing"
                                                            )}
                                                        </Alert>
                                                    )
                                                ) : null}
                                            </React.Fragment>
                                        ) : replacementItems.length ? (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {returnPolicy.allowsLesserValueExchange
                                                    ? t(
                                                          "retailReturn.lesserExchangeAllowed"
                                                      )
                                                    : t(
                                                          "retailReturn.equalOrGreaterRequired"
                                                      )}
                                            </Typography>
                                        ) : null}
                                    </Stack>
                                </Paper>
                            ) : null}

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography fontWeight={800} sx={{ mb: 1 }}>
                                    {t("retailReturn.returnedItemsTitle")}
                                </Typography>
                                <Stack divider={<Divider flexItem />}>
                                    {(returnData.items || []).map((item) => {
                                    const qty = Number(
                                        selectedQty[item.invoice_item] || 0
                                    );
                                    if (qty <= 0) return null;

                                    return (
                                        <Box
                                            key={item.invoice_item}
                                            sx={{
                                                py: 1.5,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                            }}
                                        >
                                            <Avatar
                                                variant="rounded"
                                                src={getImageUrl(item.image)}
                                                alt=""
                                                sx={{ width: 48, height: 48 }}
                                            >
                                                {String(
                                                    item.item_name ||
                                                        item.item_code
                                                )
                                                    .slice(0, 1)
                                                    .toUpperCase()}
                                            </Avatar>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography
                                                    fontWeight={700}
                                                    noWrap
                                                >
                                                    {item.item_name ||
                                                        item.item_code}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    noWrap
                                                >
                                                    {item.item_code} ·{" "}
                                                    {item.rate}{" "}
                                                    {
                                                        returnData.invoice
                                                            .currency
                                                    }
                                                </Typography>
                                            </Box>
                                            <Stack
                                                direction="row"
                                                spacing={0.5}
                                                alignItems="center"
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        updateSelectedQty(
                                                            item,
                                                            qty - 1
                                                        )
                                                    }
                                                >
                                                    <Remove fontSize="small" />
                                                </IconButton>
                                                <Typography
                                                    sx={{
                                                        minWidth: 52,
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {qty} / {item.available_qty}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    disabled={
                                                        qty >=
                                                        item.available_qty
                                                    }
                                                    onClick={() =>
                                                        updateSelectedQty(
                                                            item,
                                                            qty + 1
                                                        )
                                                    }
                                                >
                                                    <Add fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                            <IconButton
                                                color="error"
                                                onClick={() =>
                                                    updateSelectedQty(item, 0)
                                                }
                                                aria-label={t(
                                                    "retailReturn.removeItem"
                                                )}
                                            >
                                                <DeleteOutline />
                                            </IconButton>
                                        </Box>
                                    );
                                    })}
                                </Stack>
                            </Paper>

                            {!selectedItems.length ? (
                                <Alert severity="warning">
                                    {t("retailReturn.noItemsSelected")}
                                </Alert>
                            ) : null}
                        </React.Fragment>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={busy}>
                    {t("common.cancel")}
                </Button>
                <Box sx={{ flex: 1 }} />
                {returnData ? (
                    <Typography variant="body2" color="text.secondary">
                        {t("retailReturn.selectedUnits", {
                            count: selectedUnitCount,
                        })}
                    </Typography>
                ) : null}
                <Button
                    variant="contained"
                    color={returnMode === "cash" ? "error" : "primary"}
                    disabled={
                        !selectedItems.length || busy || exchangeUnavailable
                    }
                    onClick={handleSubmit}
                >
                    {submitReturnMutation.isLoading ||
                    exchangeReturnMutation.isLoading ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        t(
                            returnMode === "exchange"
                                ? "retailReturn.confirmExchange"
                                : "retailReturn.confirmReturn"
                        )
                    )}
                </Button>
            </DialogActions>
        </React.Fragment>
    );

    if (embedded) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                    backgroundColor: "background.paper",
                    borderRadius: 1,
                    overflow: "hidden",
                }}
            >
                {returnContent}
            </Box>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>{t("retailReturn.title")}</DialogTitle>
            {returnContent}
        </Dialog>
    );
}
